import {FC} from "react";
import {useTranslation} from "react-i18next";
import {Badge} from "@interstate/components/Badge";
import {Tooltip} from "@interstate/components/Tooltip";
import {
  BuildingOfficeIcon,
  LockClosedIcon,
  MegaphoneIcon,
  ShieldSolidIcon,
  XCircleIcon
} from "@interstate/components/Icons";
import {IconType} from "../../lib/interstate";
import {BadgeMetadata} from "../../badges";
import {EntityStatus} from "@common-core/coat-operational-hierarchy-appsync-model";
import "./EntityStatusBadge.scss";

export interface EntityStatusBadgeProps {
  id: string;
  status: EntityStatus;
  compact?: boolean;
  tooltip?: boolean;
}

const statusMetadata: Record<EntityStatus, BadgeMetadata<IconType>> = {
  [EntityStatus.PROTECTED]: {
    variant: "info",
    startIcon: LockClosedIcon
  },
  [EntityStatus.IN_BUSINESS]: {
    variant: "success",
    startIcon: BuildingOfficeIcon
  },
  [EntityStatus.OUT_OF_BUSINESS]: {
    variant: "error",
    startIcon: XCircleIcon
  },
  [EntityStatus.PENDING_OPENING]: {
    variant: "caution",
    startIcon: MegaphoneIcon
  },
  [EntityStatus.CONFIDENTIAL_PENDING_OPENING]: {
    variant: "caution",
    startIcon: MegaphoneIcon
  },
  [EntityStatus.CHANGE_IN_OWNERSHIP]: {
    variant: "caution",
    startIcon: MegaphoneIcon
  },
  [EntityStatus.CONFIDENTIAL_CHANGE_IN_OWNERSHIP]: {
    variant: "caution",
    startIcon: MegaphoneIcon
  },
  [EntityStatus.COMPLEX]: {
    variant: "info",
    startIcon: ShieldSolidIcon
  }
};

const translationDefaults = {
  defaultValue: "???"
};

export const EntityStatusBadge: FC<EntityStatusBadgeProps> = ({
  id,
  status,
  compact = true,
  tooltip = compact
}) => {
  const badgeId = `entity-status-${status}-${id}`;
  const iconId = `entity-status-${status}-${id}-badge-icon`;
  const fullTranslationKey = `badges.entity-status.${status}.label`;
  const translationKey = compact
    ? `badges.entity-status.${status}.mnemonic`
    : fullTranslationKey;
  const {i18n} = useTranslation();
  const {variant, startIcon: StatusIcon} = statusMetadata[status];

  const badge = (
    <Badge
      id={`${badgeId}-badge`}
      data-testid={badgeId}
      className={`entity-status-badge entity-status-badge-${status}`}
      variant={variant}
      startIcon={
        StatusIcon ? (
          <StatusIcon
            id={iconId}
            data-testid={iconId}
            className={"entity-status-badge-icon"}
          />
        ) : undefined
      }>
      <span className={"entity-status-badge-label"}>
        {i18n.t(translationKey, translationDefaults)}
      </span>
    </Badge>
  );

  return tooltip ? (
    <Tooltip
      id={`entity-status-${status}-${id}-badge-tooltip`}
      position={"top"}
      size={"small"}
      toolTipContent={i18n.t(fullTranslationKey, translationDefaults)}>
      {badge}
    </Tooltip>
  ) : (
    badge
  );
};
